import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import "../assets/css/MyDetails.css";
import "../assets/css/ChangePassword.css";
import "../assets/css/Users.css";
import "../assets/css/Permissions.css";
import axiosInstance from "../components/api/axiosInstance";
import {
  getRolePermissions,
  createRolePermission,
  updateRolePermission,
  deleteRolePermission,
} from "./rolePermissionService";

function Permissions({ onBack, onPermissionsUpdated }) {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");

  const [rolePermsByRoleId, setRolePermsByRoleId] = useState({});
  const [existingRolePermIds, setExistingRolePermIds] = useState({});
  const [loadingRolePerms, setLoadingRolePerms] = useState(false);
  const [expandedRoleIds, setExpandedRoleIds] = useState([]);

  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [permissionsDropdownOpen, setPermissionsDropdownOpen] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [modulesDropdownOpen, setModulesDropdownOpen] = useState(false);
  const [rolesDropdownOpen, setRolesDropdownOpen] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const permissionsDropdownRef = useRef(null);
  const modulesDropdownRef = useRef(null);
  const rolesDropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        permissionsDropdownRef.current &&
        !permissionsDropdownRef.current.contains(e.target)
      ) {
        setPermissionsDropdownOpen(false);
      }
      if (
        modulesDropdownRef.current &&
        !modulesDropdownRef.current.contains(e.target)
      ) {
        setModulesDropdownOpen(false);
      }
      if (
        rolesDropdownRef.current &&
        !rolesDropdownRef.current.contains(e.target)
      ) {
        setRolesDropdownOpen(false);
      }
    };
    if (permissionsDropdownOpen || modulesDropdownOpen || rolesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [permissionsDropdownOpen, modulesDropdownOpen, rolesDropdownOpen]);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await axiosInstance.get("/roles");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data?.roles)
              ? response.data.roles
              : [];

        const mapped = raw
          .map((r) => ({
            id:
              r.role_id ??
              r.roleId ??
              r.Designation_Id ??
              r.designation_id ??
              r.id,
            name:
              (r.role_name ||
                r.roleName ||
                r.Designation_Name ||
                r.designation_name ||
                r.name ||
                r.role ||
                "") + ""
          }))
          .filter((r) => r.id != null && r.name.trim() !== "");

        setRoles(mapped);
      } catch (err) {
        console.error("Error fetching roles for permissions:", err);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  // Fetch all permissions (for module list + permissions dropdown)
  useEffect(() => {
    const fetchPermissions = async () => {
      setLoadingPermissions(true);
      try {
        const response = await axiosInstance.get("/permissions");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];

        const mapped = raw
          .map((p) => ({
            id: p.Permission_Id,
            name: p.Permission_Name || "",
            module: p.Module_Name || ""
          }))
          .filter((p) => p.id != null && (p.name || "").trim() !== "");

        setAllPermissions(mapped);
      } catch (err) {
        console.error("Error fetching permissions:", err);
      } finally {
        setLoadingPermissions(false);
      }
    };
    fetchPermissions();
  }, []);

  const roleNameById = useMemo(() => {
    const map = new Map();
    for (const r of roles) {
      if (r?.id != null) map.set(String(r.id), r.name);
    }
    return map;
  }, [roles]);

  // Load cached role permissions map (fallback before API load)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("role_permissions_cache");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setRolePermsByRoleId(parsed);
      }
    } catch (_) {
      // ignore cache parse errors
    }
  }, []);

  // Fetch role permissions from API when role is selected (GET may return 405 if unsupported)
  useEffect(() => {
    if (!selectedRoleId) {
      setExistingRolePermIds({});
      return;
    }
    let mounted = true;
    setLoadingRolePerms(true);
    getRolePermissions(selectedRoleId)
      .then((result) => {
        if (!mounted) return;
        if (!result.fetched) {
          setExistingRolePermIds((prev) => ({ ...prev, [String(selectedRoleId)]: {} }));
          return;
        }
        const arr = result.data || [];
        const mapped = arr.map((p) => ({
          id: p.Permission_Id ?? p.permission_id ?? p.id ?? null,
          name: p.Permission_Name ?? p.permission_name ?? p.name ?? "",
          module: p.Module_Name ?? p.module_name ?? p.module ?? "",
          rolePermissionId: p.Role_Permission_Id ?? p.role_permission_id ?? p.RolePermission_Id ?? null,
        })).filter((p) => (p.name || "").trim() !== "" || p.id != null);

        const permIdToRpId = {};
        for (const p of mapped) {
          if (p.id != null && p.rolePermissionId != null) {
            permIdToRpId[String(p.id)] = p.rolePermissionId;
          }
        }

        setRolePermsByRoleId((prev) => {
          const next = { ...prev, [String(selectedRoleId)]: mapped };
          try {
            localStorage.setItem("role_permissions_cache", JSON.stringify(next));
          } catch (_) {}
          return next;
        });
        setExistingRolePermIds((prev) => ({
          ...prev,
          [String(selectedRoleId)]: permIdToRpId,
        }));
      })
      .catch((err) => {
        if (mounted) {
          console.error("Error fetching role permissions:", err);
          setExistingRolePermIds((prev) => ({ ...prev, [String(selectedRoleId)]: {} }));
        }
      })
      .finally(() => {
        if (mounted) setLoadingRolePerms(false);
      });
    return () => { mounted = false; };
  }, [selectedRoleId]);

  // Seed cache for current logged-in user (from signin response)
  useEffect(() => {
    try {
      const userRaw = localStorage.getItem("user");
      const permsRaw = localStorage.getItem("user_role_permissions");
      if (!userRaw || !permsRaw) return;
      const user = JSON.parse(userRaw);
      const perms = JSON.parse(permsRaw);
      const rid = user?.roles?.[0]?.role_id ?? user?.role_id ?? user?.roleId ?? null;
      if (rid == null || !Array.isArray(perms) || perms.length === 0) return;

      const mapped = perms
        .map((p) => ({
          id: p.Permission_Id ?? p.permission_id ?? p.id ?? null,
          name: p.Permission_Name ?? p.permission_name ?? p.name ?? "",
          module: p.Module_Name ?? p.module_name ?? p.module ?? ""
        }))
        .filter((p) => (p.name || "").trim() !== "" || p.id != null);

      setRolePermsByRoleId((prev) => {
        const next = { ...prev, [String(rid)]: mapped };
        try {
          localStorage.setItem("role_permissions_cache", JSON.stringify(next));
        } catch (_) {
          // ignore
        }
        return next;
      });
    } catch (_) {
      // ignore
    }
  }, []);

  const togglePermission = (id) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const getRolePermsGrouped = (roleId) => {
    const list = rolePermsByRoleId[String(roleId)] || [];
    return list.reduce((acc, p) => {
      const key = (p.module || "Other").trim() || "Other";
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
      return acc;
    }, {});
  };

  // Prefill selections when role permissions load
  useEffect(() => {
    setError("");
    setSuccess("");
    setPermissionsDropdownOpen(false);
    setModulesDropdownOpen(false);
    if (!selectedRoleId) {
      setSelectedPermissionIds([]);
      setSelectedModules([]);
      return;
    }
    const current = rolePermsByRoleId[String(selectedRoleId)] || [];
    const permIds = current
      .map((p) => p.id)
      .filter((id) => id != null)
      .map((id) => Number(id));
    setSelectedPermissionIds(Array.from(new Set(permIds)));
    const mods = current.map((p) => p.module).filter(Boolean);
    setSelectedModules(Array.from(new Set(mods)));
  }, [selectedRoleId, rolePermsByRoleId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedRoleId) {
      setError("Please select a role.");
      return;
    }
    if (selectedPermissionIds.length === 0) {
      setError("Please select at least one permission.");
      return;
    }

    const roleId = Number(selectedRoleId);
    const rk = String(selectedRoleId);
    const existing = rolePermsByRoleId[rk] || [];
    const existingPermIds = new Set(existing.map((p) => String(p.id)).filter(Boolean));
    const existingRpMap = existingRolePermIds[rk] || {};
    const selectedSet = new Set(selectedPermissionIds.map((id) => String(id)));

    const toAdd = selectedPermissionIds.filter((id) => !existingPermIds.has(String(id)));
    const toRemove = existing
      .filter((p) => p.id != null && !selectedSet.has(String(p.id)) && p.rolePermissionId != null)
      .map((p) => ({ permissionId: p.id, rolePermissionId: p.rolePermissionId }));

    setSaving(true);
    try {
      const roleIdNum = Number(selectedRoleId);
      const converted = [];
      const toAddFinal = [];
      const toRemoveFinal = [];

      if (toRemove.length > 0 && toAdd.length > 0) {
        const convertCount = Math.min(toRemove.length, toAdd.length);
        for (let i = 0; i < convertCount; i++) {
          converted.push({
            rolePermissionId: toRemove[i].rolePermissionId,
            newPermissionId: toAdd[i],
          });
        }
        toAddFinal.push(...toAdd.slice(convertCount));
        toRemoveFinal.push(...toRemove.slice(convertCount));
      } else {
        toAddFinal.push(...toAdd);
        toRemoveFinal.push(...toRemove);
      }

      const promises = [
        ...converted.map((c) =>
          updateRolePermission(c.rolePermissionId, roleIdNum, c.newPermissionId)
        ),
        ...toRemoveFinal.map((r) => deleteRolePermission(r.rolePermissionId)),
      ];
      if (toAddFinal.length > 0) {
        promises.push(createRolePermission(roleIdNum, toAddFinal));
      }
      await Promise.all(promises);

      setSuccess("Permissions saved successfully for this role.");

      const isCurrentUserRole = (() => {
        try {
          const userRaw = localStorage.getItem("user");
          if (!userRaw) return false;
          const user = JSON.parse(userRaw);
          const primaryRole = user?.roles?.[0];
          const rid =
            primaryRole?.role_id ??
            primaryRole?.Role_Id ??
            primaryRole?.Designation_Id ??
            primaryRole?.designation_id ??
            primaryRole?.id ??
            user?.role_id ??
            user?.Role_Id ??
            user?.roleId ??
            null;
          if (rid != null && String(rid) === String(selectedRoleId)) return true;
          const roleForSelected = roles.find((r) => String(r.id) === String(selectedRoleId));
          const currentRoleName = (
            primaryRole?.role_name ??
            primaryRole?.name ??
            user?.role_name ??
            user?.role ??
            ""
          ).toString().trim();
          const selectedRoleName = (roleForSelected?.name ?? "").toString().trim();
          return (
            currentRoleName.length > 0 &&
            selectedRoleName.length > 0 &&
            currentRoleName.toLowerCase() === selectedRoleName.toLowerCase()
          );
        } catch (_) {
          return false;
        }
      })();
      if (isCurrentUserRole && onPermissionsUpdated) {
        const mapped = selectedPermissionIds
          .map((id) => allPermissions.find((p) => String(p.id) === String(id)))
          .filter(Boolean)
          .map((p) => ({
            Module_Name: p.module || "",
            Permission_Name: p.name || "",
            Permission_Id: p.id,
            module_name: p.module,
            permission_name: p.name,
            permission_id: p.id,
          }));
        try {
          localStorage.setItem("user_role_permissions", JSON.stringify(mapped));
          setRolePermsByRoleId((prev) => {
            const next = { ...prev, [rk]: mapped.map((p) => ({ id: p.Permission_Id, name: p.Permission_Name, module: p.Module_Name, rolePermissionId: null })) };
            localStorage.setItem("role_permissions_cache", JSON.stringify(next));
            return next;
          });
          onPermissionsUpdated();
        } catch (_) {}
      }

      getRolePermissions(selectedRoleId).then((result) => {
        if (result.fetched && Array.isArray(result.data)) {
          const arr = result.data;
          const mapped = arr.map((p) => ({
            id: p.Permission_Id ?? p.permission_id ?? p.id ?? null,
            name: p.Permission_Name ?? p.permission_name ?? p.name ?? "",
            module: p.Module_Name ?? p.module_name ?? p.module ?? "",
            rolePermissionId: p.Role_Permission_Id ?? p.role_permission_id ?? p.RolePermission_Id ?? null,
          })).filter((p) => (p.name || "").trim() !== "" || p.id != null);

          const permIdToRpId = {};
          for (const p of mapped) {
            if (p.id != null && p.rolePermissionId != null) {
              permIdToRpId[String(p.id)] = p.rolePermissionId;
            }
          }

          setRolePermsByRoleId((prev) => {
            const next = { ...prev, [rk]: mapped };
            try {
              localStorage.setItem("role_permissions_cache", JSON.stringify(next));
            } catch (_) {}
            return next;
          });
          setExistingRolePermIds((prev) => ({ ...prev, [rk]: permIdToRpId }));
        } else {
          // GET not supported (405) - optimistic update from current selection
          const mapped = selectedPermissionIds
            .map((id) => allPermissions.find((p) => String(p.id) === String(id)))
            .filter(Boolean)
            .map((p) => ({
              id: p.id,
              name: p.name ?? "",
              module: p.module ?? "",
              rolePermissionId: null,
            }));
          setRolePermsByRoleId((prev) => {
            const next = { ...prev, [rk]: mapped };
            try {
              localStorage.setItem("role_permissions_cache", JSON.stringify(next));
            } catch (_) {}
            return next;
          });
        }
      });
    } catch (err) {
      console.error("Error saving role permissions:", err);
      const msg =
        err.response?.data?.message ??
        err.response?.data?.error ??
        err.message ??
        "Failed to save permissions. Please try again.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const modulesList = useMemo(
    () => Array.from(new Set(allPermissions.map((p) => p.module).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [allPermissions]
  );

  const filteredPermissions = useMemo(
    () =>
      allPermissions.filter((perm) =>
        selectedModules.length === 0 ? true : selectedModules.includes(perm.module)
      ),
    [allPermissions, selectedModules]
  );

  const handleToggleAllFilteredPermissions = () => {
    setSelectedPermissionIds((prev) => {
      const allIds = filteredPermissions
        .map((p) => (p.id != null ? Number(p.id) : null))
        .filter((id) => id != null && !Number.isNaN(id));

      if (allIds.length === 0) return prev;

      const allSelected = allIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !allIds.includes(id));
      }

      return Array.from(new Set([...prev, ...allIds]));
    });
  };

  const handleToggleAllModules = () => {
    setSelectedModules((prev) => {
      if (modulesList.length === 0) return prev;
      const allSelected = modulesList.every((m) => prev.includes(m));
      if (allSelected) {
        return [];
      }
      return [...modulesList];
    });
  };

  const toggleExpandedRole = (roleId) => {
    setExpandedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((x) => x !== roleId) : [...prev, roleId]
    );
  };

  return (
    <div className="mydetails-container">
      <div className="mydetails-header">
        <button className="back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>

      <div className="mydetails-form">
        <div className="form-section">
          <div className="permissions-page-header">
            <h2 className="section-title">Manage Role Permissions</h2>
          </div>

          <form onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-grid permissions-form-grid">
              <div className="form-group" ref={rolesDropdownRef}>
                <label>
                  Role <span className="required">*</span>
                </label>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (!loadingRoles) setRolesDropdownOpen((open) => !open);
                    }
                  }}
                  className="permissions-select-trigger"
                  onClick={() => !loadingRoles && setRolesDropdownOpen((open) => !open)}
                  style={{ opacity: loadingRoles ? 0.6 : 1, cursor: loadingRoles ? "default" : "pointer" }}
                >
                  <span className="permissions-select-text">
                    {loadingRoles
                      ? "Loading roles..."
                      : selectedRoleId
                        ? (roles.find((r) => String(r.id) === String(selectedRoleId))?.name || "Select Role")
                        : "Select Role"}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${rolesDropdownOpen ? "open" : ""}`}
                  />
                </div>
                {rolesDropdownOpen && !loadingRoles && (
                  <div className="permissions-select-panel">
                    {roles.map((role) => (
                      <label
                        key={role.id}
                        className="permissions-select-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRoleId(String(role.id));
                          setRolesDropdownOpen(false);
                        }}
                      >
                        <input
                          type="radio"
                          name="selectedRole"
                          checked={String(selectedRoleId) === String(role.id)}
                          onChange={() => {
                            setSelectedRoleId(String(role.id));
                            setRolesDropdownOpen(false);
                          }}
                        />
                        <span className="permissions-select-item-label">
                          {role.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Modules multi-select dropdown (beside role) */}
              <div className="form-group" ref={modulesDropdownRef}>
                <label>Modules</label>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setModulesDropdownOpen((open) => !open);
                    }
                  }}
                  className="permissions-select-trigger"
                  onClick={() => setModulesDropdownOpen((open) => !open)}
                >
                  <span className="permissions-select-text">
                    {loadingPermissions
                      ? "Loading modules..."
                      : selectedModules.length === 0
                        ? "Select Modules"
                        : `${selectedModules.length} module(s) selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${modulesDropdownOpen ? "open" : ""}`}
                  />
                </div>
                {modulesDropdownOpen && (
                  <div className="permissions-select-panel">
                    {(() => {
                      const allSelected =
                        modulesList.length > 0 &&
                        modulesList.every((m) => selectedModules.includes(m));
                      return (
                        <>
                          {modulesList.length > 0 && (
                            <label
                              className="permissions-select-item"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={handleToggleAllModules}
                              />
                              <span className="permissions-select-item-label">
                                Select All
                              </span>
                            </label>
                          )}
                          {modulesList.map((moduleName) => (
                            <label
                              key={moduleName}
                              className="permissions-select-item"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedModules.includes(moduleName)}
                                onChange={() =>
                                  setSelectedModules((prev) =>
                                    prev.includes(moduleName)
                                      ? prev.filter((m) => m !== moduleName)
                                      : [...prev, moduleName]
                                  )
                                }
                              />
                              <span className="permissions-select-item-label">
                                {moduleName}
                              </span>
                            </label>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Permissions multi-select dropdown */}
              <div className="form-group" ref={permissionsDropdownRef}>
                <label>
                  Select Permissions <span className="required">*</span>
                </label>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (!loadingRolePerms) setPermissionsDropdownOpen((open) => !open);
                    }
                  }}
                  className="permissions-select-trigger"
                  onClick={() => !loadingRolePerms && setPermissionsDropdownOpen((open) => !open)}
                  style={{ opacity: loadingRolePerms ? 0.7 : 1, cursor: loadingRolePerms ? "wait" : "pointer" }}
                >
                  <span className="permissions-select-text">
                    {loadingRolePerms && selectedRoleId
                      ? "Loading permissions..."
                      : selectedPermissionIds.length === 0
                        ? "Select Permissions"
                        : `${selectedPermissionIds.length} permission(s) selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${permissionsDropdownOpen ? "open" : ""}`}
                  />
                </div>
                {permissionsDropdownOpen && (
                  <div className="permissions-select-panel">
                    {!loadingPermissions && allPermissions.length === 0 && (
                      <div style={{ padding: "10px 12px", color: "#777", fontSize: "14px" }}>
                        No permissions found.
                      </div>
                    )}
                    {(() => {
                      const allIds = filteredPermissions
                        .map((p) => (p.id != null ? Number(p.id) : null))
                        .filter((id) => id != null && !Number.isNaN(id));
                      const allSelected =
                        allIds.length > 0 && allIds.every((id) => selectedPermissionIds.includes(id));
                      return (
                        <>
                          {allIds.length > 0 && (
                            <label
                              className="permissions-select-item"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={handleToggleAllFilteredPermissions}
                              />
                              <span className="permissions-select-item-label">
                                Select All
                              </span>
                            </label>
                          )}
                          {filteredPermissions.map((perm) => (
                            <label
                              key={perm.id}
                              className="permissions-select-item"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissionIds.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                              />
                              <span className="permissions-select-item-label">
                                {perm.module ? `${perm.module} – ${perm.name}` : perm.name}
                              </span>
                            </label>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Save button - 4th column */}
              <div className="form-group permissions-save-group">
                <label>&nbsp;</label>
                <button
                  type="submit"
                  className="btn-add-user"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Permissions"}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="form-section">
          <h2 className="section-title">Role Access Overview</h2>
          {loadingRoles ? (
            <div className="permissions-muted">Loading roles...</div>
          ) : roles.length === 0 ? (
            <div className="permissions-muted">No roles found.</div>
          ) : (
            <div className="permissions-table-wrap">
              <table className="permissions-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Modules & Permissions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((r) => {
                    const grouped = r?.id != null ? getRolePermsGrouped(r.id) : {};
                    const moduleNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                    const roleNameFromList = r?.id != null ? roleNameById.get(String(r.id)) : null;
                    const isExpanded = r?.id != null && expandedRoleIds.includes(r.id);

                    return (
                      <React.Fragment key={r.id}>
                        <tr>
                          <td className="permissions-td-nowrap">
                            {roleNameFromList ||
                              r.name ||
                              (r.id != null ? `Role #${r.id}` : "—")}
                          </td>
                          <td>
                            {moduleNames.length === 0 ? (
                              <span className="permissions-muted">
                                No permissions assigned for this role
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="permissions-view-link"
                                onClick={() => toggleExpandedRole(r.id)}
                              >
                                {isExpanded ? "Hide Modules & Permissions" : "View Modules & Permissions"}
                              </button>
                            )}
                          </td>
                        </tr>

                        {isExpanded && moduleNames.length > 0 && (
                          <tr className="permissions-expanded-row">
                            <td colSpan={2}>
                              <div className="permissions-user-access">
                                {moduleNames.map((m) => {
                                  const perms = grouped[m]
                                    .slice()
                                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                    .map((p) => p.name || `#${p.id}`);
                                  return (
                                    <div key={m} className="permissions-user-module">
                                      <span className="permissions-user-module-name">{m}:</span>
                                      <span className="permissions-user-module-perms">{perms.join(", ")}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Permissions;

